import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 2C7.239 2 5 4.239 5 7V11L3 13V14H17V13L15 11V7C15 4.239 12.761 2 10 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.5 14C7.5 14.92 7.81 15.77 8.33 16.43C8.85 17.09 9.59 17.5 10.4 17.5C11.21 17.5 11.95 17.09 12.47 16.43C12.99 15.77 13.3 14.92 13.3 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 4H14M5.333 4V2.667C5.333 2.298 5.632 2 6 2H10C10.368 2 10.667 2.298 10.667 2.667V4M12.667 4V13.333C12.667 13.702 12.368 14 12 14H4C3.632 14 3.333 13.702 3.333 13.333V4" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-bg-secondary border border-border-color rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-text-primary mb-2">{title}</h3>
        <p className="text-text-secondary mb-6">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 px-4 bg-bg-tertiary hover:bg-bg-primary text-text-primary rounded-lg font-medium transition-colors">
            Отмена
          </button>
          <button onClick={onConfirm} className="flex-1 py-2.5 px-4 bg-error hover:bg-red-600 text-white rounded-lg font-medium transition-colors">
            Удалить
          </button>
        </div>
      </div>
    </div>
  );
};

export const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [notificationRewards, setNotificationRewards] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [rewardsTotal, setRewardsTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rewardsPage, setRewardsPage] = useState(0);
  const [toolFilter, setToolFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });
  const limit = 20;

  useEffect(() => {
    fetchNotifications();
    fetchStats();
    fetchRewards();
  }, [page, toolFilter, statusFilter, rewardsPage]);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const params = {
        limit,
        offset: page * limit,
      };
      if (toolFilter !== 'all') params.tool_name = toolFilter;
      if (statusFilter !== 'all') params.status = statusFilter;

      const data = await api.getNotifications(params);
      
      setNotifications(data.notifications || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Ошибка загрузки уведомлений:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRewards = async () => {
    try {
      const params = {
        limit,
        offset: rewardsPage * limit,
      };
      const data = await api.getNotificationRewards(params);
      setNotificationRewards(data.rewards || []);
      setRewardsTotal(data.total || 0);
    } catch (error) {
      console.error('Ошибка загрузки наград:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await api.getNotificationStats();
      setStats(data);
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };

  const handleDelete = (id) => {
    setConfirmModal({ isOpen: true, id });
  };

  const handleConfirmDelete = async () => {
    if (confirmModal.id) {
      try {
        await api.deleteNotification(confirmModal.id);
        fetchNotifications();
        fetchStats();
      } catch (error) {
        console.error('Ошибка удаления:', error);
      }
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  const handleViewUser = (vkUserId) => {
    navigate(`/users?search=${vkUserId}`);
  };

  const getToolDisplayName = (toolName) => {
    const names = {
      'webdev': 'Веб-разработка',
      'search': 'Поиск в интернете',
    };
    return names[toolName] || toolName;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-primary">Уведомления</h1>
        <div className="text-sm text-text-secondary">
          Всего: <span className="text-text-primary font-medium">{total}</span>
        </div>
      </div>

      {/* Блок 1: Награды за включение уведомлений */}
      {stats?.rewards && (
        <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">✨ Награды за уведомления</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-bg-tertiary rounded-lg p-4">
              <div className="text-sm text-text-secondary mb-1">Получили награду</div>
              <div className="text-2xl font-bold text-text-primary">{stats.rewards.total_users}</div>
              <div className="text-xs text-text-muted">человек</div>
            </div>
            <div className="bg-bg-tertiary rounded-lg p-4">
              <div className="text-sm text-text-secondary mb-1">Всего звёзд</div>
              <div className="text-2xl font-bold text-amber-400">{stats.rewards.total_stars}</div>
              <div className="text-xs text-text-muted">выдано</div>
            </div>
            <div className="bg-bg-tertiary rounded-lg p-4">
              <div className="text-sm text-text-secondary mb-1">За сегодня</div>
              <div className="text-2xl font-bold text-success">{stats.rewards.today.stars}</div>
              <div className="text-xs text-text-muted">{stats.rewards.today.users} чел.</div>
            </div>
            <div className="bg-bg-tertiary rounded-lg p-4">
              <div className="text-sm text-text-secondary mb-1">За неделю</div>
              <div className="text-2xl font-bold text-success">{stats.rewards.week.stars}</div>
              <div className="text-xs text-text-muted">{stats.rewards.week.users} чел.</div>
            </div>
            <div className="bg-bg-tertiary rounded-lg p-4">
              <div className="text-sm text-text-secondary mb-1">За месяц</div>
              <div className="text-2xl font-bold text-success">{stats.rewards.month.stars}</div>
              <div className="text-xs text-text-muted">{stats.rewards.month.users} чел.</div>
            </div>
          </div>
        </div>
      )}

      {/* Таблица: Награды за уведомления */}
      {stats?.rewards && stats.rewards.total_users > 0 && (
        <div className="bg-bg-secondary rounded-xl border border-border-color overflow-hidden">
          <div className="px-4 py-3 border-b border-border-color">
            <h3 className="text-lg font-semibold text-text-primary">✨ Получили награду за уведомления</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-bg-tertiary border-b border-border-color">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">VK User ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Награда</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Дата получения</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-color">
                {isLoading ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-text-secondary">
                      <div className="animate-spin h-6 w-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                    </td>
                  </tr>
                ) : notificationRewards.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-text-secondary">
                      Нет данных о наградах
                    </td>
                  </tr>
                ) : (
                  notificationRewards.map((reward, index) => (
                    <tr key={index} className="hover:bg-bg-tertiary/50 transition-colors">
                      <td className="px-4 py-3 text-sm text-text-primary font-medium">
                        {reward.vk_user_id}
                      </td>
                      <td className="px-4 py-3 text-sm text-amber-400 font-medium">
                        +{reward.reward_amount} ⭐
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {new Date(reward.rewarded_at).toLocaleString('ru-RU')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Пагинация для наград */}
          {rewardsTotal > limit && (
            <div className="px-4 py-3 border-t border-border-color flex items-center justify-between">
              <div className="text-sm text-text-secondary">
                Показано {rewardsPage * limit + 1}-{Math.min((rewardsPage + 1) * limit, rewardsTotal)} из {rewardsTotal}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setRewardsPage(p => Math.max(0, p - 1))}
                  disabled={rewardsPage === 0}
                  className="px-3 py-1.5 bg-bg-tertiary hover:bg-bg-primary text-text-primary rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                 ← Назад
                </button>
                <button
                  onClick={() => setRewardsPage(p => (rewardsPage + 1) * limit < rewardsTotal ? p + 1 : p)}
                  disabled={(rewardsPage + 1) * limit >= rewardsTotal}
                  className="px-3 py-1.5 bg-bg-tertiary hover:bg-bg-primary text-text-primary rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Вперёд →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Блок 2: Подписки по инструментам */}
      {stats && stats.by_tool && stats.by_tool.length > 0 && (
        <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">🔔 Подписки по инструментам</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-bg-tertiary rounded-lg p-4">
              <div className="text-sm text-text-secondary mb-1">Всего подписок</div>
              <div className="text-3xl font-bold text-text-primary">{stats.total}</div>
            </div>
            <div className="bg-bg-tertiary rounded-lg p-4">
              <div className="text-sm text-text-secondary mb-1">Разрешили</div>
              <div className="text-3xl font-bold text-success">{stats.allowed}</div>
            </div>
            <div className="bg-bg-tertiary rounded-lg p-4">
              <div className="text-sm text-text-secondary mb-1">Не разрешили</div>
              <div className="text-3xl font-bold text-error">{stats.not_allowed}</div>
            </div>
          </div>
          
          {/* ��о инструментам */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.by_tool.map((tool) => (
              <div key={tool.tool_name} className="bg-bg-tertiary rounded-lg p-3">
                <div className="text-sm text-text-secondary mb-1">{getToolDisplayName(tool.tool_name)}</div>
                <div className="text-xl font-bold text-text-primary">{tool.count}</div>
                <div className="text-xs text-text-muted">разрешили: {tool.allowed_count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Если нет данных о подписках по инструментам */}
      {stats && (!stats.by_tool || stats.by_tool.length === 0) && (
        <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">🔔 Подписки по инструментам</h3>
          <div className="text-text-secondary text-center py-8">
            Нет данных о подписках на уведомления по инструментам
          </div>
        </div>
      )}

      {/* Фильтры */}
      <div className="flex flex-col sm:flex-row gap-4">
        <select
          value={toolFilter}
          onChange={(e) => { setToolFilter(e.target.value); setPage(0); }}
          className="px-4 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary"
        >
          <option value="all">Все инструменты</option>
          <option value="webdev">Веб-разработка</option>
          <option value="search">Поиск в интернете</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="px-4 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary"
        >
          <option value="all">Все статусы</option>
          <option value="allowed">Разрешили уведомления</option>
          <option value="not_allowed">Не разрешили</option>
        </select>
      </div>

      {/* Таблица */}
      <div className="bg-bg-secondary rounded-xl border border-border-color overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-bg-tertiary border-b border-border-color">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <BellIcon />
                    <span>Инструмент</span>
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">VK User ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Статус</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Дата подписки</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-color">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-text-secondary">
                    <div className="animate-spin h-6 w-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                  </td>
                </tr>
              ) : notifications.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-text-secondary">
                    Уведомления не найдены
                  </td>
                </tr>
              ) : (
                notifications.map((notif) => (
                  <tr key={notif.id} className="hover:bg-bg-tertiary/50 transition-colors">
                    <td className="px-4 py-3 text-sm text-text-primary font-medium">
                      {getToolDisplayName(notif.tool_name)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => handleViewUser(notif.vk_user_id)}
                        className="text-accent-primary hover:underline font-mono"
                      >
                        {notif.vk_user_id}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        notif.notification_allowed
                          ? 'bg-success/10 text-success'
                          : 'bg-error/10 text-error'
                      }`}>
                        {notif.notification_allowed ? 'Разрешил' : 'Не разрешил'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {new Date(notif.subscribed_at).toLocaleString('ru-RU')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(notif.id)}
                        className="p-1.5 text-text-secondary hover:text-error transition-colors inline-flex"
                        title="Удалить"
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-secondary">
            Страница {page + 1} из {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-4 py-2 bg-bg-secondary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
            >
              Назад
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-4 py-2 bg-bg-secondary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
            >
              Вперёд
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Удалить подписку?"
        message="Эта подписка будет удалена безвозвратно."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmModal({ isOpen: false, id: null })}
      />
    </div>
  );
};
